import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, Search, Filter, Pencil, Trash2, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, Calendar, Tag, Users, Target,
  LayoutGrid, Table2, ExternalLink, ArrowUpRight, DollarSign,
  Layers, CheckCircle, XCircle, Archive, FileCode2, Download,
  AlertCircle, PauseCircle, Clock
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { utils, writeFile } from 'xlsx';

interface MVPMetrics {
  visits: number;
  contacts: number;
  estimatedMonthlyIncome: number;
}

interface MVP {
  _id: string;
  mvpId: string;
  name: string;
  description: string;
  category: string;
  targetAudience: string;
  createdFrom?: string;
  tags: string[];
  status: string;
  notificationEmail?: string;
  owner: string;
  createdAt: string;
  updatedAt: string;
  metrics: MVPMetrics;
}

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface MVPsResponse {
  status: string;
  data: {
    mvps: MVP[];
    pagination: PaginationInfo;
  };
}

interface FilterState {
  name: string;
  category: string;
  status: string;
  tags: string[];
  minVisits: number;
  minContacts: number;
  minIncome: number;
}

interface ApiSuccess {
  status: 'success';
  message: string;
  data: {
    mvpId: string;
  };
}

interface ApiError {
  status: 'error';
  message: string;
  error?: {
    message: string;
    stack?: string;
  };
}

type ApiResponse = ApiSuccess | ApiError;

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

const ITEMS_PER_PAGE = 10;

const STATUS_COLORS = {
  'active': {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    icon: CheckCircle,
    label: 'Activo'
  },
  'inactive': {
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-200',
    icon: PauseCircle,
    label: 'Inactivo'
  },
  'draft': {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: Clock,
    label: 'Borrador'
  },
  'archived': {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    icon: Archive,
    label: 'Archivado'
  }
} as const;

// Tipo para los estados válidos
type ValidStatus = keyof typeof STATUS_COLORS;

// Función para verificar si un estado es válido
const isValidStatus = (status: string): status is ValidStatus => {
  return status in STATUS_COLORS;
};

// Función para obtener el estilo del estado
const getStatusStyle = (status: string) => {
  if (isValidStatus(status)) {
    return STATUS_COLORS[status];
  }
  // Estilo por defecto si el estado no es válido
  return {
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
    icon: AlertCircle,
    label: status || 'Desconocido'
  };
};

export default function MVPs() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FilterState>({
    name: '',
    category: '',
    status: '',
    tags: [],
    minVisits: 0,
    minContacts: 0,
    minIncome: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMVPName, setDeleteMVPName] = useState('');
  const [mvpToDelete, setMvpToDelete] = useState<MVP | null>(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });
  
  const queryClient = useQueryClient();

  // Fetch MVPs
  const { data, isLoading, isError } = useQuery<MVPsResponse>({
    queryKey: ['mvps', page, filters],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      queryParams.append('page', page.toString());
      queryParams.append('limit', ITEMS_PER_PAGE.toString());
      
      // Add filter params if they have values
      if (filters.name) queryParams.append('name', filters.name);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.tags.length > 0) {
        filters.tags.forEach(tag => queryParams.append('tags', tag));
      }

      const response = await fetch(`${API_URL}/mvps?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Error fetching MVPs');
      }
      return response.json();
    },
  });

  // Delete MVP mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/mvps/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const responseData = await response.json() as ApiResponse;
      
      if (!response.ok || responseData.status === 'error') {
        throw new Error(
          responseData.status === 'error'
            ? responseData.error?.message || responseData.message
            : 'Error al eliminar el MVP'
        );
      }
      
      return responseData;
    },
    onSuccess: (response) => {
      // Invalidar la consulta para recargar la lista
      queryClient.invalidateQueries({ queryKey: ['mvps'] });
      setShowDeleteModal(false);
      setMvpToDelete(null);
      setDeleteMVPName('');

      // Mostrar mensaje de éxito
      const successMessage = document.createElement('div');
      successMessage.className = 'fixed top-4 right-4 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50';
      successMessage.innerHTML = `
        <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <p class="font-medium">${response.message}</p>
      `;
      document.body.appendChild(successMessage);

      // Remover el mensaje después de 5 segundos
      setTimeout(() => {
        successMessage.remove();
      }, 5000);
    },
    onError: (error: Error) => {
      // Mostrar mensaje de error
      const errorMessage = document.createElement('div');
      errorMessage.className = 'fixed top-4 right-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50';
      errorMessage.innerHTML = `
        <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <p class="font-medium">Error al eliminar el MVP</p>
          <p class="text-sm text-red-600">${error.message}</p>
        </div>
      `;
      document.body.appendChild(errorMessage);

      // Remover el mensaje después de 5 segundos
      setTimeout(() => {
        errorMessage.remove();
      }, 5000);

      setShowDeleteModal(false);
      setMvpToDelete(null);
      setDeleteMVPName('');
    },
  });

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedMVPs = useMemo(() => {
    if (!data?.data.mvps) return [];
    
    const filtered = data.data.mvps.filter(mvp => {
      const nameMatch = mvp.name.toLowerCase().includes(filters.name.toLowerCase());
      const categoryMatch = !filters.category || mvp.category.toLowerCase() === filters.category.toLowerCase();
      const statusMatch = !filters.status || mvp.status.toLowerCase() === filters.status.toLowerCase();
      const visitsMatch = !filters.minVisits || mvp.metrics.visits >= filters.minVisits;
      const contactsMatch = !filters.minContacts || mvp.metrics.contacts >= filters.minContacts;
      const incomeMatch = !filters.minIncome || mvp.metrics.estimatedMonthlyIncome >= filters.minIncome;
      
      return nameMatch && categoryMatch && statusMatch && visitsMatch && contactsMatch && incomeMatch;
    });
    
    return filtered.sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.key) {
        case 'name':
          return direction * a.name.localeCompare(b.name);
        case 'category':
          return direction * a.category.localeCompare(b.category);
        case 'status':
          return direction * a.status.localeCompare(b.status);
        case 'visits':
          return direction * (a.metrics.visits - b.metrics.visits);
        case 'contacts':
          return direction * (a.metrics.contacts - b.metrics.contacts);
        case 'income':
          return direction * (a.metrics.estimatedMonthlyIncome - b.metrics.estimatedMonthlyIncome);
        case 'createdAt':
          return direction * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        default:
          return 0;
      }
    });
  }, [data?.data.mvps, sortConfig, filters]);

  const handleDeleteClick = (mvp: MVP) => {
    setMvpToDelete(mvp);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (mvpToDelete && deleteMVPName === mvpToDelete.name) {
      deleteMutation.mutateAsync(mvpToDelete._id);
    }
  };

  const totalPages = data?.data.pagination.pages || 1;

  const statsData = useMemo(() => {
    if (!data?.data.mvps) return {
      totalMVPs: 0,
      activeMVPs: 0,
      totalVisits: 0,
      totalContacts: 0,
      totalIncome: 0,
      statusCount: {
        active: 0,
        inactive: 0,
        draft: 0,
        archived: 0
      }
    };

    const stats = {
      totalMVPs: data.data.mvps.length,
      activeMVPs: data.data.mvps.filter(mvp => mvp.status === 'active').length,
      totalVisits: data.data.mvps.reduce((sum, mvp) => sum + mvp.metrics.visits, 0),
      totalContacts: data.data.mvps.reduce((sum, mvp) => sum + mvp.metrics.contacts, 0),
      totalIncome: data.data.mvps.reduce((sum, mvp) => sum + mvp.metrics.estimatedMonthlyIncome, 0),
      statusCount: {
        active: data.data.mvps.filter(mvp => mvp.status === 'active').length,
        inactive: data.data.mvps.filter(mvp => mvp.status === 'inactive').length,
        draft: data.data.mvps.filter(mvp => mvp.status === 'draft').length,
        archived: data.data.mvps.filter(mvp => mvp.status === 'archived').length
      }
    };

    return stats;
  }, [data?.data.mvps]);

  const handleExportToExcel = () => {
    if (!data?.data.mvps) return;

    // Preparar los datos para Excel
    const excelData = data.data.mvps.map(mvp => ({
      'ID': mvp.mvpId,
      'Nombre': mvp.name,
      'Descripción': mvp.description,
      'Categoría': mvp.category,
      'Estado': mvp.status,
      'Audiencia Objetivo': mvp.targetAudience,
      'Etiquetas': mvp.tags.join(', '),
      'Email de Notificación': mvp.notificationEmail || 'No especificado',
      'Propietario': mvp.owner,
      'Visitas': mvp.metrics.visits,
      'Contactos': mvp.metrics.contacts,
      'Ingresos Mensuales Estimados': `$${mvp.metrics.estimatedMonthlyIncome.toLocaleString()}`,
      'Tasa de Conversión (%)': ((mvp.metrics.contacts / mvp.metrics.visits) * 100).toFixed(2) + '%',
      'Ingreso por Contacto': `$${(mvp.metrics.estimatedMonthlyIncome / mvp.metrics.contacts).toFixed(2)}`,
      'Fecha de Creación': format(new Date(mvp.createdAt), 'dd/MM/yyyy HH:mm', { locale: es }),
      'Última Actualización': format(new Date(mvp.updatedAt), 'dd/MM/yyyy HH:mm', { locale: es })
    }));

    // Crear el libro de Excel
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Ajustar el ancho de las columnas
    const columnWidths = [
      { wch: 10 },  // ID
      { wch: 30 },  // Nombre
      { wch: 50 },  // Descripción
      { wch: 15 },  // Categoría
      { wch: 12 },  // Estado
      { wch: 30 },  // Audiencia Objetivo
      { wch: 30 },  // Etiquetas
      { wch: 30 },  // Email
      { wch: 20 },  // Propietario
      { wch: 12 },  // Visitas
      { wch: 12 },  // Contactos
      { wch: 20 },  // Ingresos
      { wch: 15 },  // Tasa de Conversión
      { wch: 15 },  // Ingreso por Contacto
      { wch: 20 },  // Fecha Creación
      { wch: 20 },  // Última Actualización
    ];
    ws['!cols'] = columnWidths;

    // Agregar la hoja al libro
    utils.book_append_sheet(wb, ws, 'MVPs');

    // Generar el nombre del archivo con la fecha actual
    const fileName = `MVPs_Export_${format(new Date(), 'dd-MM-yyyy_HH-mm', { locale: es })}.xlsx`;

    // Descargar el archivo
    writeFile(wb, fileName);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">MVPs</h1>
            <p className="mt-1 text-sm text-slate-500">
              Gestiona y monitorea tus MVPs
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportToExcel}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
              disabled={isLoading || isError || !data?.data.mvps}
            >
              <Download className="w-5 h-5" />
              Exportar a Excel
            </button>
            <button 
              onClick={() => navigate('/mvps/new')}
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuevo MVP
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total MVPs */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total MVPs</p>
                <p className="text-2xl font-bold text-slate-900">{statsData.totalMVPs}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xs text-slate-500">
                    {statsData.statusCount.active} activos
                  </span>
                  <span className="text-xs text-slate-400">•</span>
                  <span className="text-xs text-slate-500">
                    {statsData.statusCount.draft} borradores
                  </span>
                </div>
              </div>
              <div className="p-2 bg-primary-50 rounded-lg">
                <Layers className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </div>

          {/* Total Visits */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Visitas</p>
                <p className="text-2xl font-bold text-blue-600">{statsData.totalVisits.toLocaleString()}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Promedio: {statsData.totalMVPs ? Math.round(statsData.totalVisits / statsData.totalMVPs).toLocaleString() : 0} por MVP
                </p>
              </div>
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Total Contacts */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Contactos</p>
                <p className="text-2xl font-bold text-emerald-600">{statsData.totalContacts.toLocaleString()}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Promedio: {statsData.totalMVPs ? Math.round(statsData.totalContacts / statsData.totalMVPs).toLocaleString() : 0} por MVP
                </p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg">
                <Target className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>

          {/* Total Income */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Ingresos Mensuales</p>
                <p className="text-2xl font-bold text-amber-600">${statsData.totalIncome.toLocaleString()}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Promedio: ${statsData.totalMVPs ? Math.round(statsData.totalIncome / statsData.totalMVPs).toLocaleString() : 0} por MVP
                </p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <div className="p-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center h-64 text-red-600">
                Error al cargar los MVPs
              </div>
            ) : (
              /* Table View */
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-y border-slate-200">
                      <th className="text-left py-3 px-4">
                        <div className="space-y-2">
                          <button 
                            className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                            onClick={() => handleSort('name')}
                          >
                            Nombre
                            {sortConfig.key === 'name' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <input
                            type="text"
                            value={filters.name}
                            onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Filtrar por nombre"
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      </th>
                      <th className="text-left py-3 px-4">
                        <div className="space-y-2">
                          <button 
                            className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                            onClick={() => handleSort('category')}
                          >
                            Categoría
                            {sortConfig.key === 'category' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <select
                            value={filters.category}
                            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="">Todas</option>
                            <option value="saas">SaaS</option>
                            <option value="ecommerce">E-commerce</option>
                            <option value="marketplace">Marketplace</option>
                          </select>
                        </div>
                      </th>
                      <th className="text-left py-3 px-4">
                        <div className="space-y-2">
                          <button 
                            className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                            onClick={() => handleSort('status')}
                          >
                            Estado
                            {sortConfig.key === 'status' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="">Todos</option>
                            <option value="active">Activo</option>
                            <option value="inactive">Inactivo</option>
                            <option value="draft">Borrador</option>
                            <option value="archived">Archivado</option>
                          </select>
                        </div>
                      </th>
                      <th className="text-left py-3 px-4">
                        <div className="space-y-2">
                          <button 
                            className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                            onClick={() => handleSort('visits')}
                          >
                            Métricas
                            {sortConfig.key === 'visits' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={filters.minVisits || ''}
                              onChange={(e) => setFilters(prev => ({ ...prev, minVisits: Number(e.target.value) }))}
                              placeholder="Min. visitas"
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={filters.minContacts || ''}
                              onChange={(e) => setFilters(prev => ({ ...prev, minContacts: Number(e.target.value) }))}
                              placeholder="Min. contactos"
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={filters.minIncome || ''}
                              onChange={(e) => setFilters(prev => ({ ...prev, minIncome: Number(e.target.value) }))}
                              placeholder="Min. ingresos"
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                        </div>
                      </th>
                      <th className="text-left py-3 px-4">
                        <span className="text-sm font-semibold text-slate-600">Información</span>
                      </th>
                      <th className="text-right py-3 px-4">
                        <span className="text-sm font-semibold text-slate-600">Acciones</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMVPs.map((mvp) => (
                      <tr key={mvp._id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <div>
                            <Link
                              to={`/mvps/${mvp._id}`}
                              className="font-medium text-slate-900 hover:text-primary-600"
                            >
                              {mvp.name}
                            </Link>
                            <div className="text-sm text-slate-500">{mvp.description}</div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-600 capitalize">{mvp.category}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            {/* Status Badge */}
                            {(() => {
                              const statusStyle = getStatusStyle(mvp.status);
                              return (
                                <div className={`
                                  inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium border
                                  ${statusStyle.bg}
                                  ${statusStyle.text}
                                  ${statusStyle.border}
                                `}>
                                  {React.createElement(statusStyle.icon, {
                                    className: 'w-3.5 h-3.5'
                                  })}
                                  <span className="capitalize">{statusStyle.label}</span>
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Users className="w-4 h-4 text-blue-500" />
                              <span>{mvp.metrics.visits.toLocaleString()} visitas</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Target className="w-4 h-4 text-emerald-500" />
                              <span>{mvp.metrics.contacts.toLocaleString()} contactos</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <DollarSign className="w-4 h-4 text-amber-500" />
                              <span>${mvp.metrics.estimatedMonthlyIncome.toLocaleString()} ingresos mensuales</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Calendar className="w-4 h-4 text-purple-500" />
                              <span>{format(new Date(mvp.createdAt), 'dd MMM yyyy', { locale: es })}</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {mvp.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200"
                                >
                                  <Tag className="w-3 h-3" />
                                  {tag}
                                </span>
                              ))}
                              {mvp.tags.length === 0 && (
                                <span className="text-sm text-slate-400 italic">
                                  Sin etiquetas
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/mvps/${mvp._id}/edit`)}
                              className="p-1 text-slate-600 hover:text-primary-600 transition-colors"
                              title="Editar"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(mvp)}
                              className="p-1 text-slate-600 hover:text-red-600 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => window.open(`/mvps/${mvp._id}`, '_blank')}
                              className="p-1 text-slate-600 hover:text-primary-600 transition-colors"
                              title="Ver MVP"
                            >
                              <ExternalLink className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && !isError && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(page * ITEMS_PER_PAGE, data?.data.pagination.total || 0)} de {data?.data.pagination.total} resultados
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 text-slate-600 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-slate-600">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-slate-600 hover:text-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Confirmar eliminación
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Esta acción no se puede deshacer. Para confirmar, escribe el nombre del MVP: 
                <span className="font-semibold">{mvpToDelete?.name}</span>
              </p>
              <input
                type="text"
                value={deleteMVPName}
                onChange={(e) => setDeleteMVPName(e.target.value)}
                placeholder="Nombre del MVP"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setMvpToDelete(null);
                    setDeleteMVPName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteMVPName !== mvpToDelete?.name || deleteMutation.isPending}
                  className={`
                    px-4 py-2 text-sm font-medium text-white rounded-lg
                    ${deleteMutation.isPending
                      ? 'bg-red-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700'
                    }
                  `}
                >
                  {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar MVP'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
} 